/**
 * Custom report period (SPEC-CIS-REPORT-CUSTOM-PERIOD-001 section 4.5) -- the
 * pure parts the Weekly report screen shares with App.jsx: client-side
 * validation in the section 4.1 order, the server-code messages, the view key
 * that tells a period apart from a week in caches and refs, and the same
 * "dd Mmm - dd Mmm yyyy" label the server's archive rows carry.
 *
 * Nothing here talks to the server. The server stays authoritative: every
 * rule below is checked again there and refused with the named code; a
 * mismatch between the two (for example the project's time zone making
 * "today" a different date) always resolves in the server's favour.
 */

import { normalisePublishedNumber } from "./reportLabel.js";

// The server's MAX_PERIOD_DAYS (azure-functions/cis/shared/report-period.js).
// Changing it is a spec amendment, so it is a single constant here too.
export const MAX_PERIOD_DAYS = 92;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function isCalendarDate(value) {
  if (typeof value !== "string" || !DATE_RE.test(value)) return false;
  const d = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value;
}

function periodDays(from, to) {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000) + 1;
}

/** "01 Sep - 30 Sep 2026" (both years when they differ), as the server's periodLabel. */
export function formatPeriodLabel(periodStart, periodEnd) {
  if (!isCalendarDate(periodStart) || !isCalendarDate(periodEnd)) return "";
  const dd = (iso) => iso.slice(8, 10);
  const mm = (iso) => MONTHS_SHORT[Number(iso.slice(5, 7)) - 1];
  const first = `${dd(periodStart)} ${mm(periodStart)}`;
  const last = `${dd(periodEnd)} ${mm(periodEnd)} ${periodEnd.slice(0, 4)}`;
  return periodStart.slice(0, 4) === periodEnd.slice(0, 4)
    ? `${first} – ${last}`
    : `${first} ${periodStart.slice(0, 4)} – ${last}`;
}

/** "1 Sep - 30 Sep" -- the short span of the "Covers ..." tag. */
export function formatCoversSpan(periodStart, periodEnd) {
  if (!isCalendarDate(periodStart) || !isCalendarDate(periodEnd)) return "";
  const part = (iso) => `${Number(iso.slice(8, 10))} ${MONTHS_SHORT[Number(iso.slice(5, 7)) - 1]}`;
  return `${part(periodStart)} – ${part(periodEnd)}`;
}

export const PERIOD_MESSAGES = {
  both: "Choose both dates",
  order: "From must be on or before To",
  tooLong: `A report can cover at most ${MAX_PERIOD_DAYS} days`,
  future: "To date cannot be in the future",
};

/**
 * Client-side validation, in the section 4.1 order. `null` = valid, or
 * nothing entered yet (an untouched form shows no complaint). Returns the
 * message to show.
 */
export function validatePeriodInput({ from, to, today }) {
  const hasFrom = Boolean(from);
  const hasTo = Boolean(to);
  if (!hasFrom && !hasTo) return null;
  if (!hasFrom || !hasTo) return PERIOD_MESSAGES.both;
  if (!isCalendarDate(from) || !isCalendarDate(to) || from > to) return PERIOD_MESSAGES.order;
  if (periodDays(from, to) > MAX_PERIOD_DAYS) return PERIOD_MESSAGES.tooLong;
  if (today && to > today) return PERIOD_MESSAGES.future;
  return null;
}

/**
 * The message for a server refusal of the period (readiness read or
 * generate): `period_invalid`, `period_too_long`, `period_in_future`,
 * `period_overlap` (409, body names the conflicting report and its period).
 * `null` for any other code.
 */
export function periodServerMessage(errorCode, conflict) {
  switch (errorCode) {
    case "period_invalid": return PERIOD_MESSAGES.order;
    case "period_too_long": return PERIOD_MESSAGES.tooLong;
    case "period_in_future": return PERIOD_MESSAGES.future;
    case "period_overlap": {
      if (!conflict) return "These dates overlap another report";
      const label = conflict.periodLabel || formatPeriodLabel(conflict.periodStart, conflict.periodEnd);
      const what = conflict.reportStatus === "Draft" ? "a draft" : conflict.reportStatus === "Approved" ? "an approved report" : conflict.reportStatus === "Issued" ? (normalisePublishedNumber(conflict.publishedNumber) ? `report ${normalisePublishedNumber(conflict.publishedNumber)}` : "an issued report") : "another report";
      return `These dates overlap ${what}${label ? ` (${label})` : ""}`;
    }
    default: return null;
  }
}

export function isPeriodErrorCode(errorCode) {
  return typeof errorCode === "string" && errorCode.startsWith("period_") && periodServerMessage(errorCode) !== null;
}

/** "2026-09-01..2026-09-30" -- the cache/ref key of a period view (a week's key is its Monday). */
export function periodKey(period) {
  return period && period.periodStart && period.periodEnd ? `${period.periodStart}..${period.periodEnd}` : "";
}

/** Inverse of `periodKey`; `null` for a week key. */
export function parsePeriodKey(key) {
  const m = /^(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/.exec(String(key || ""));
  return m ? { periodStart: m[1], periodEnd: m[2] } : null;
}

/** The view key of an envelope: its period for a custom view, else its week. */
export function reportEnvelopeKey(envelope) {
  if (!envelope) return "";
  if (envelope.periodKind === "custom") return periodKey(envelope);
  return envelope.weekStart || "";
}
